/**
 * Client API cho module Email Crawler.
 *
 * Mọi hàm nhận `apiBaseUrl` (API base runtime — prop `apiBaseUrl` khi S3 Explorer được nhúng
 * vào frontend host, vd `https://dev-hoaphat.cagent.cmcts.ai/s3-explorer`) và ghép URL tuyệt
 * đối, KHÔNG dựa vào `axios.defaults.baseURL`: bundle đã build của package không biết domain
 * triển khai nên mặc định sẽ rơi về `http://localhost:3000` (xem `lib/config.ts`).
 */

import axios from './axios';

export type EmailCrawlerProcessorType = 'email_dashboard' | 'recruitment';

export interface EmailCrawlerSourceAuth {
  status: string | null;
  reauthReason: string | null;
  authenticatedAccountEmail: string | null;
  sourceMailboxAddress: string;
}

export interface EmailCrawlerSource {
  id: string;
  sourceKey: string;
  displayName: string;
  mailboxAddress: string;
  processorType: EmailCrawlerProcessorType;
  enabled: boolean;
  scheduleCron: string;
  timezone: string;
  lookbackMinutes: number;
  maxEmailsPerRun: number;
  s3Prefix: string;
  uiFolderPrefix: string | null;
  subjectFilters: string[];
  senderFilters: string[];
  settings: Record<string, unknown>;
  lastDueCycleKey: string | null;
  lastCompletedCycleKey: string | null;
  lastRunStatus: string | null;
  lastRunAt: string | null;
  auth: EmailCrawlerSourceAuth | null;
}

export interface EmailCrawlerRun {
  id: string;
  sourceId: string;
  cycleKey: string;
  status: string;
  triggerType: string;
  windowStart: string;
  windowEnd: string;
  stats: Record<string, number>;
  errorMessage: string | null;
  safeErrorCode: string | null;
  startedAt: string;
  completedAt: string | null;
}

export interface EmailCrawlerSourceListResponse {
  sources: EmailCrawlerSource[];
}

export interface EmailCrawlerSourceByFolderResponse {
  source: EmailCrawlerSource | null;
}

export interface EmailCrawlerSourceDetailResponse {
  source: EmailCrawlerSource;
  runs: EmailCrawlerRun[];
}

export interface EmailCrawlerRunResponse {
  status: string;
  run: EmailCrawlerRun;
  result: 'created' | 'existing';
}

function extractError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    const data = err.response.data as { error?: string; message?: string };
    if (data.error) return data.error;
    if (data.message) return data.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export async function listEmailCrawlerSources(
  apiBaseUrl: string,
): Promise<EmailCrawlerSource[]> {
  const response = await axios.get<EmailCrawlerSourceListResponse>(
    `${apiBaseUrl}/api/email-crawler/sources`,
  );
  return response.data?.sources ?? [];
}

export async function getEmailCrawlerSourceByKey(
  apiBaseUrl: string,
  sourceKey: string,
): Promise<EmailCrawlerSourceDetailResponse> {
  const response = await axios.get<EmailCrawlerSourceDetailResponse>(
    `${apiBaseUrl}/api/email-crawler/sources/${encodeURIComponent(sourceKey)}`,
  );
  return response.data;
}

export async function resolveEmailCrawlerSourceByFolder(
  apiBaseUrl: string,
  prefix: string,
): Promise<EmailCrawlerSourceByFolderResponse> {
  const response = await axios.get<EmailCrawlerSourceByFolderResponse>(
    `${apiBaseUrl}/api/email-crawler/sources/by-folder`,
    { params: { prefix } },
  );
  return response.data;
}

export interface CreateEmailCrawlerSourceInput {
  sourceKey: string;
  displayName?: string;
  mailboxAddress: string;
  processorType: EmailCrawlerProcessorType;
  enabled?: boolean;
  scheduleCron?: string;
  timezone?: string;
  lookbackMinutes?: number;
  maxEmailsPerRun?: number;
  s3Prefix?: string;
  uiFolderPrefix?: string | null;
  subjectFilters?: string[];
  senderFilters?: string[];
  settings?: Record<string, unknown>;
}

export interface UpdateEmailCrawlerSourceInput {
  displayName?: string;
  mailboxAddress?: string;
  enabled?: boolean;
  scheduleCron?: string;
  timezone?: string;
  lookbackMinutes?: number;
  maxEmailsPerRun?: number;
  s3Prefix?: string;
  uiFolderPrefix?: string | null;
  subjectFilters?: string[];
  senderFilters?: string[];
  settings?: Record<string, unknown>;
}

export async function createEmailCrawlerSource(
  apiBaseUrl: string,
  input: CreateEmailCrawlerSourceInput,
): Promise<EmailCrawlerSource> {
  try {
    const response = await axios.post<{ source: EmailCrawlerSource }>(
      `${apiBaseUrl}/api/email-crawler/sources`,
      input,
    );
    return response.data.source;
  } catch (err) {
    throw new Error(extractError(err, 'Failed to create crawler source'));
  }
}

export async function updateEmailCrawlerSource(
  apiBaseUrl: string,
  sourceKey: string,
  input: UpdateEmailCrawlerSourceInput,
): Promise<EmailCrawlerSource> {
  try {
    const response = await axios.put<{ source: EmailCrawlerSource }>(
      `${apiBaseUrl}/api/email-crawler/sources/${encodeURIComponent(sourceKey)}`,
      input,
    );
    return response.data.source;
  } catch (err) {
    throw new Error(extractError(err, 'Failed to update crawler source'));
  }
}

export async function runEmailCrawlerSource(
  apiBaseUrl: string,
  sourceKey: string,
): Promise<EmailCrawlerRunResponse> {
  try {
    const response = await axios.post<EmailCrawlerRunResponse>(
      `${apiBaseUrl}/api/email-crawler/sources/${encodeURIComponent(sourceKey)}/run`,
    );
    return response.data;
  } catch (err) {
    throw new Error(extractError(err, 'Failed to run crawler source'));
  }
}

export async function listEmailCrawlerSourceRuns(
  apiBaseUrl: string,
  sourceKey: string,
  limit = 25,
): Promise<EmailCrawlerRun[]> {
  const response = await axios.get<{ runs: EmailCrawlerRun[] }>(
    `${apiBaseUrl}/api/email-crawler/sources/${encodeURIComponent(sourceKey)}/runs`,
    { params: { limit } },
  );
  return response.data?.runs ?? [];
}

export interface RecruitmentOutlookStatus {
  configured: boolean;
  missingVars: string[];
}

export async function getRecruitmentOutlookStatus(
  apiBaseUrl: string,
): Promise<RecruitmentOutlookStatus> {
  const response = await axios.get<RecruitmentOutlookStatus>(
    `${apiBaseUrl}/api/email-crawler/recruitment-outlook-status`,
  );
  return response.data;
}
